<template>
  <BoxWithTitle>
    <RedirectModal
      :show="showRedirectModal"
      @close="showRedirectModal = false"
    />
    <template #header>
      <v-toolbar-title>
        {{ $t('flowDiagram.pageName') }} {{ networkName }}
      </v-toolbar-title>
    </template>

    <template #icons>
      <v-btn
        v-if="hasPermission('flowDiagrams.add') || hasPermission('flowDiagrams.edit')"
        text
        :disabled="!isSavable"
        @click="onClickSave"
      >
        {{ $t('forms.btn.save') }}
      </v-btn>
    </template>

    <template #content>
      <v-row
        align="center"
        justify="center"
      >
        <v-col>
          <VFileDragAndDrop
            v-if="hasPermission('flowDiagrams.add')"
            :file="newFlowDiagram.diagram"
            :mimes="mimes"
            :showPdfResizeMsg="true"
            @onChange="onChange"
            @removeFile="removeFile"
          />
          <div>
            <VTableLocal
              :items="displayedFiles"
              :isSortable="false"
              :disablePagination="true"
              :headers="tableHeaders"
              @iconAction="onClickAction"
            >
              <template #body="{items}">
                <draggable
                  :list="items"
                  :options="{disabled: false}"
                  tag="tbody"

                  @change="onDragChange"
                >
                  <tr
                    v-for="(item,index) in items"
                    :key="index"
                  >
                    <template v-for="(headerN,indexN) in tableHeaders">
                      <td
                        v-if="headerN.value === 'drag_drop'"
                        :key="`${index}_${indexN}_drag`"
                      >
                        <template>
                          <v-icon
                            large
                            class="hidden-md-and-down"
                            color="primary"
                          >
                            mdi-drag-vertical
                          </v-icon>
                          <span @click="activateTooltip(item.order)"> {{ item.order }} </span>
                        </template>
                      </td>
                      <td
                        v-else-if="headerN.value==='action'"
                        :key="`${index}_${indexN}_action`"
                      >
                        <template v-if="actions">
                          <div class="action-buttons">
                            <VTableAction
                              :itemActions="actions"
                              :verticalActionDots="true"
                              :isCustomIcon="true"
                              @iconAction="(action)=>onClickAction(item, action)"
                            />
                          </div>
                        </template>
                      </td>
                      <td
                        v-else-if="headerN.value==='fileName'"
                        :key="`${index}_${indexN}_others`"
                      >
                        <span>{{ item[headerN.value] }}</span>
                        <div
                          v-if="isTooltipActive(item.order)"
                          class="custom-drag-input"
                        >
                          <div class="wrapper-div">
                            <v-text-field
                              v-model="customDragItemPosition"
                              @keydown.enter="onManualPositionInput(item)"
                            />
                          </div>
                        </div>
                      </td>
                      <td
                        v-else
                        :key="`${index}_${indexN}_others`"
                      >
                        {{ item[headerN.value] }}
                      </td>
                    </template>
                  </tr>
                </draggable>
              </template>
            </VTableLocal>
          </div>
        </v-col>
      </v-row>
    </template>
  </BoxWithTitle>
</template>

<script>
import Vue from 'vue';
import { mapGetters, mapActions } from 'vuex';
import { isEmpty } from 'lodash';
import BoxWithTitle from '@/components/shared/containers/VBoxWithTitle';
import ConfirmForm from '@/components/shared/widgets/ConfirmForm';
import VTableLocal from '@/components/shared/content-views/VTableLocal';
import VFileDragAndDrop from '@/components/shared/widgets/forms/VuetifyInputs/VFileDragAndDrop';
import draggable from 'vuedraggable';
import VTableAction from '@/components/shared/widgets/VTableAction';
// eslint-disable-next-line import/no-cycle
import lockService from '@/utilities/lockService';
import RedirectModal from '@/components/shared/RedirectModal.vue';


export default {
    components: {
        RedirectModal,
        BoxWithTitle,
        VTableLocal,
        VFileDragAndDrop,
        draggable,
        VTableAction,

    },
    data() {
        return {
            showRedirectModal: false,
            flowDiagram: null,
            isSavable: false,
            newFlowDiagram: {
                diagram: null,
            },
            files: [],
            showTooltipIndex: -1,
            mimes: ['application/pdf', 'image/png', 'image/gif', 'image/jpeg'],
            localComponentId: null,
            actionName: 'flowDiagram',
        };
    },
    computed: {
        ...mapGetters([
            'network',
            'component',
            'hasPermission',
            'lang',
            'preventQuitFormular',
        ]),
        rules() {
            return {
                size: 10000,
                mimes: this.mimes,
            };
        },
        isNew() {
            return isEmpty(this.flowDiagram);
        },
        networkName() {
            const networkName = `${this.network.name} (${this.network.type.key})`;
            if (!isEmpty(this.component)) {
                return `${networkName} -> ${this.component.name} (${this.component.type.key})`;
            }

            return networkName;
        },
        tableHeaders() {
            return [
                'drag_drop',
                'fileName',
                'size',
                'uploadedBy',
                'addedDate',
                'action',
            ].map(value => ({
                text: value === 'drag_drop' ? '' : this.$t(`flowDiagram.${value}`),
                value,
            }));
        },
        displayedFiles() {
            return this.flowDiagram?.diagram.length ? this.flowDiagram.diagram.map(d => ({
                ...d,
                size: `${Math.floor(d.size / 1000)}KB`,
                addedDate: this.$options.filters.formatDate(d.createdAt),
            })) : [];
        },
        actions() {
            const actions = [];

            if (this.hasPermission('files.view')) {
                actions.push({
                    name: this.$t('flowDiagram.preview'),
                    icon: 'mdi-eye',
                });
            }

            if (this.hasPermission('files.remove')) {
                actions.push({
                    name: this.$t('flowDiagram.deleteIcon'),
                    icon: 'delete',
                    iconAndTextColor: 'red',
                });
            }

            return actions;
        },

    },
    async beforeRouteLeave(to, from, next) {
        try {
            if (this.localComponentId) {
                const releaseStatus = await lockService.releaseLock(this.localComponentId, this.actionName);
                if (!releaseStatus) return next(false);
            }
        } catch (error) {
            console.error('Error releasing lock on route leave:', error);
            // Continue navigation even if lock release fails
        }
        return next();
    },
    watch: {
        isSavable: {
            handler() {
                if (this.isSavable) {
                    this.activePreventQuitFormular();
                }
            },
        },
        'newFlowDiagram.diagram': {
            handler() {
                if (this.isSavable) {
                    this.activePreventQuitFormular();
                } else {
                    this.disablePreventQuitFormular();
                }
            },
        },
    },
    beforeDestroy() {
        window.removeEventListener('beforeunload', this.handleUnload);
        // Ensure locks are released even if beforeRouteLeave didn't complete
        if (this.localComponentId) {
            lockService.releaseLock(this.localComponentId, this.actionName)
                .catch(err => console.error('Error releasing lock on destroy:', err));
        }
    },
    mounted() {
        window.addEventListener('beforeunload', this.handleUnload);
    },
    async created() {
        this.localComponentId = this.$route.params.componentId || this.component?.id;
        if (this.network.id) {
            await this.loadData();
        }
        if (!this.$store.getters.lockFrom) {
            await this.checkLockOnLoad();
        }
    },
    methods: {
        ...mapActions({
            disablePreventQuitFormular: 'disablePreventQuitFormular',
            activePreventQuitFormular: 'activePreventQuitFormular',
        }),
        handleUnload() {
            if (this.localComponentId) {
                // Handle synchronously for page unload events
                try {
                    lockService.releaseLock(this.localComponentId, this.actionName);
                } catch (error) {
                    console.error('Error releasing lock on unload:', error);
                }
            }
        },
        async checkLockOnLoad() {
            try {
                const lockAvailable = await lockService.getLockStatus(this.localComponentId, this.actionName);
                if (!lockAvailable) {
                    this.showRedirectModal = true;
                    return;
                }
                await lockService.requestLock(this.localComponentId, this.actionName);
            } catch (error) {
                this.showRedirectModal = true;
            }
        },
        async onClickSave() {
            this.isSavable = false;
            // check here
            if (this.isNew) {
                return this.addFlowDiagram();
            }

            return this.updateFlowDiagram();
        },
        preparePayload() {
            const formData = new FormData();
            formData.append('diagram', this.newFlowDiagram.diagram);
            if (!isEmpty(this.component)) { formData.append('component', this.component.id); }
            return formData;
        },
        async loadData() {
            const queryParams = !isEmpty(this.component.id) ? { component: this.component.id } : {};
            const { data } = await this.$rq.getFlowDiagrams(this.network.id, queryParams);
            [this.flowDiagram] = data;
        },
        async addFlowDiagram() {
            const payload = this.preparePayload();
            const { data } = await this.$rq.addFlowDiagram(this.network.id, payload);
            this.flowDiagram = data;

            this.$toasted.global.toast_success({
                message: this.$t('flowDiagram.added'),
            });
            await this.resetAll();
        },
        async updateFlowDiagram() {
            const payload = this.preparePayload();
            await this.$rq.updateFlowDiagram(this.network.id, this.flowDiagram.id, payload);

            this.$toasted.global.toast_success({
                message: this.$t('flowDiagram.updated'),
            });
            this.resetAll();
        },
        onClickDeleteFlowDiagram(file) {
            if (!this.flowDiagram || !this.hasPermission('files.remove') || !this.hasPermission('flowDiagrams.remove')) {
                return;
            }

            this.$root.$emit('showModal', {
                title: this.$t('flowDiagram.delete'),
                component: Vue.component('ConfirmForm', ConfirmForm),
                payload: { msg: this.$t('flowDiagram.confirmRemove') },
                onConfirm: () => this.deleteFlowDiagram(file),
                isForm: false,
            });
        },
        async deleteFlowDiagram(file) {
            await this.$rq.deleteFlowDiagram(this.network.id, this.flowDiagram.id, file.id);
            this.flowDiagram = null;

            this.$toasted.global.toast_success({
                message: this.$t('flowDiagram.deleted'),
            });
            await this.resetAll();
        },
        onChange(file) {
            this.newFlowDiagram.diagram = file;
            this.isSavable = true;
        },
        removeFile() {
            this.newFlowDiagram.diagram = null;
            this.isSavable = false;
        },
        async resetAll() {
            this.newFlowDiagram.diagram = null;
            this.isSavable = false;
            await this.loadData();
        },
        async previewFile(file) {
            if (this.hasPermission('files.view')) {
                const { id, fileName } = file;
                const { data } = await this.$rq.downloadFile(id);
                this.$utils.saveFileOnDisk(data, fileName);
            }
        },
        async onClickAction(item, action) {
            switch (action.icon) {
                case 'mdi-eye':
                    await this.previewFile(item);
                    break;
                case 'delete':
                    this.onClickDeleteFlowDiagram(item);
                    break;
                default:
                    break;
            }
        },
        async onDragChange(e) {
            const { element: file, newIndex } = e.moved;
            this.showTooltipIndex = -1;
            await this.$rq.updateFlowDiagramPosition(this.network.id, this.flowDiagram.id, file.id, { newIndex });
            await this.loadData();
        },
        onManualPositionInput(item) {
            if (this.customDragItemPosition !== this.showTooltipIndex) {
                this.onDragChange({ moved: { element: item, newIndex: this.customDragItemPosition } });
            }
        },
        isTooltipActive(currentIndex) {
            return this.showTooltipIndex === currentIndex;
        },
        activateTooltip(currentIndex) {
            this.showTooltipIndex = currentIndex;
            this.customDragItemPosition = currentIndex;
        },
    },
};

</script>
<style lang='scss' scoped>
  .custom-drag-input {
        position: relative;
        width: 18%;
        .wrapper-div{
          position: absolute;
          top: 8px;
          height: 33px;
          padding: 0 5px;
          outline: 1px solid;
          background-color: white;
          .v-text-field{
            padding-top: 0;
            margin-top: 0;
          }
        }
      }

  @media screen and (max-width: 800px) {
    .drag-container {
      overflow: scroll ;
      margin-left: 55px !important;
      padding-left: 0;
    }
  }

</style>
